import { IssueTaskDoc, IssueTaskStatus } from '@gooduncles/gu-app-schema';
import { DatePicker, Input, Spin } from 'antd';
import dayjs from 'dayjs';
import { FC } from 'react';
import { issueTaskCategoriesForTargetDate, issueTaskStatusForTargetDate } from 'src/constants/issue.constants';

import { updateIssueTask } from 'src/utils/firebase/firebase-short-cut';
import { needPublicNoteSetting } from 'src/utils/issue/issue-util';

import useDebouncedUpdate from 'src/hooks/useDebouncedUpdate';
import useUserById from 'src/hooks/swr/useUserById';

import { InfoItem } from 'src/atomic-components/atoms/IssueInfoItem';

interface IssueTaskInfoSectionProps {
  issueTaskDoc: IssueTaskDoc;
  editable: boolean;
}

const IssueTaskInfoSection: FC<IssueTaskInfoSectionProps> = ({ issueTaskDoc, editable }) => {
  const { user, userLoading } = useUserById(issueTaskDoc.creatorId);

  // 로컬 상태 + debounced 업데이트를 함께 관리
  const [publicNote, setPublicNote] = useDebouncedUpdate(
    issueTaskDoc.publicNote ?? '',
    async (value: string) => {
      try {
        await updateIssueTask(issueTaskDoc._id, { publicNote: value || null });
      } catch (error) {
        console.error('Failed to update publicNote:', error);
      }
    },
    1000
  );

  const [privateNote, setPrivateNote] = useDebouncedUpdate(
    issueTaskDoc.privateNote ?? '',
    async (value: string) => {
      try {
        await updateIssueTask(issueTaskDoc._id, { privateNote: value || null });
      } catch (error) {
        console.error('Failed to update privateNote:', error);
      }
    },
    1000
  );

  const [targetDate, setTargetDate] = useDebouncedUpdate(
    issueTaskDoc.targetDate ?? null,
    async (value: string | null) => {
      try {
        await updateIssueTask(issueTaskDoc._id, { targetDate: value });
      } catch (error) {
        console.error('Failed to update targetDate:', error);
      }
    },
    1000
  );

  return (
    <div>
      {/* Task가 자동 처리에 잡히거나(IN_PROGRESS), 고객한테 노출될 때(COMPLETED)의 기준 날짜 */}
      <InfoItem
        label='적용 날짜'
        value={
          editable ? (
            <DatePicker
              value={targetDate ? dayjs(targetDate) : null}
              onChange={(date) => setTargetDate(date ? date.format('YYYY-MM-DD') : null)}
              disabled={
                issueTaskDoc.status >= IssueTaskStatus.FAILED ||
                !issueTaskStatusForTargetDate.includes(issueTaskDoc.status)
              }
              disabledDate={
                issueTaskCategoriesForTargetDate.includes(issueTaskDoc.category)
                  ? (current) => current && current < dayjs().startOf('day')
                  : undefined
              }
              format='YYYY-MM-DD'
              placeholder='날짜 선택'
              size='small'
              style={{ width: 216 }}
            />
          ) : (
            <span>{targetDate || '-'}</span>
          )
        }
        tooltip='Task가 자동 처리에 잡히거나(IN_PROGRESS), 고객한테 노출될 때(COMPLETED)의 기준 날짜'
      />
      <InfoItem label='완료시각' value={issueTaskDoc.finishedAt || '-'} />
      <InfoItem
        label='작성자'
        value={userLoading ? <Spin size='small' /> : user?.email?.split('@')[0] ?? issueTaskDoc.creatorType}
      />

      {needPublicNoteSetting(issueTaskDoc.category) && (
        <InfoItem
          label='고객노출 메모'
          value={
            editable ? (
              <Input
                size='small'
                placeholder='고객노출 메모'
                value={publicNote}
                allowClear
                onChange={(e) => setPublicNote(e.target.value)}
                style={{ width: 216 }}
              />
            ) : (
              <span>{publicNote}</span>
            )
          }
        />
      )}
      <InfoItem
        label='내부 메모'
        value={
          editable ? (
            <Input
              size='small'
              placeholder='내부 메모'
              value={privateNote}
              allowClear
              onChange={(e) => setPrivateNote(e.target.value)}
              style={{ width: 216 }}
            />
          ) : (
            <span>{privateNote}</span>
          )
        }
      />
    </div>
  );
};

export default IssueTaskInfoSection;
export type { IssueTaskInfoSectionProps };
